import { createBrowserRouter } from "react-router-dom";
import {
  Root,
  ErrorFallback,
  Home,
  NotFound,
  UnAuthorized,
  AdminProfile,
  AdminThemeSettings,
  LoginPage,
  SignupPage,
  LinkExpiredPage,
  AdminStoreSettings,
  AdminIntegrations,
  SocialReturnLanding,
  NotificationsPage,
  RequirePermission,
  TemplatesRoutePage,
  BillingPage,
  OnboardingGate,
  PaymentSettingsPage,
  StripeReturnPage,
  StripeRefreshPage,
  SystemAdminHome,
  PublicLegalDocumentPage,
} from "./imports";
import { StudioRouteSwitch } from "@/features/template-studio-host";
import Auth0Callback from "@/features/auth/ui/Auth0Callback";
import BetaBillingGuard from "@/features/billing/components/BetaBillingGuard";
// `RequirePermission` (from ./imports) is the auth/layout guard at the
// parent route — checks Auth0 session + active store. `RouteGuard` here is
// the resource:action permission gate used on individual child routes.
import { env } from "@/shared/config/env";
import RouteGuard from "./routerPermission";
import UserRoutes from "./routes/userRoutes";
import CustomerRoutes from "./routes/customerRoutes";
import OrderRoutes from "./routes/orderRoutes";
import ReturnRoutes from "./routes/returnRoutes";
import CategoriesRoutes from "./routes/categoriesRoutes";
import MaterialsRoutes from "./routes/materialsRoutes";
import variantRoute from "./routes/variantRoutes";
import productRoute from "./routes/productRoutes";
import RolesRoutes from "./routes/rolesRoutes";
import TransactionRoutes from "./routes/transactionRoutes";
import AnalyticsRoutes from "./routes/analyticsRoutes";
import SystemStoreRoutes from "./routes/systemStoreRoutes";
import SystemBetaAccessRoutes from "./routes/systemBetaAccessRoutes";
import FeaturedProductsRoutes from "./routes/featuredProductsRoutes";
import SystemPlanRoutes from "./routes/systemPlanRoutes";
import SystemSubscriptionRoutes from "./routes/systemSubscriptionRoutes";
import SystemVariantsRoutes from "./routes/systemVariantsRoutes";
import SystemSuperCategoryRoutes from "./routes/systemSuperCategoryRoutes";
import SystemLogisticsRoutes from "./routes/systemLogisticsRoutes";
import SystemPaymentRoutes from "./routes/systemPaymentRoutes";
import SystemMediaRoutes from "./routes/systemMediaRoutes";
import SystemAnalyticsRoutes from "./routes/systemAnalyticsRoutes";
import MerchantLogisticsRoutes from "./routes/merchantLogisticsRoutes";
import MyRequestsRoutes from "./routes/myRequestsRoutes";
import HelpSupportRoutes from "./routes/helpSupportRoutes";
import SystemLegalSupportRoutes from "./routes/systemLegalSupportRoutes";
import { lazy, Suspense } from "react";
import RouteErrorBoundary from "@/shared/ui/error/RouteErrorBoundary";
import SuspenseLoading from "@/shared/ui/loading/suspenseLoading/SuspenseLoading";
import TemplateGallerySkeleton from "@/shared/ui/skeleton/templateGallerySkeleton/TemplateGallerySkeleton";
import SystemDocumentVerificationRoutes from "./routes/systemDocumentVerificationRoutes";
import SystemSupportTicketsRoutes from "./routes/systemSupportTicketsRoutes";
import SystemSupportFaqRoutes from "./routes/systemSupportFaqRoutes";

const VendorLegalSupportPage = lazy(
  () => import("@/pages/vendor/legal-support/LegalSupportPage"),
);

const router = createBrowserRouter([
  {
    path: "",
    element: <Root />,
    errorElement: <ErrorFallback />,
    children: [
      { path: "", element: <Home /> },
      { path: "login", element: <LoginPage /> },
      { path: "signup", element: <SignupPage /> },
      {
        path: "privacy-policy",
        element: <PublicLegalDocumentPage documentType="PRIVACY_POLICY" />,
      },
      {
        path: "terms-and-conditions",
        element: (
          <PublicLegalDocumentPage documentType="TERMS_AND_CONDITIONS" />
        ),
      },
      { path: "link-expired", element: <LinkExpiredPage /> },

      { path: "/callback", element: <Auth0Callback /> },

      // ── Social authorization landing routes ────────────────────────────
      // `redirectPath` on POST /social/:platform/connect is matched against a
      // server-side allowlist by EXACT string — no prefix matching, and a
      // third value needs a backend change. The two accepted values are bare
      // paths that this dashboard does not otherwise have, because every
      // merchant screen is store-scoped. Both are mounted here so the
      // outcome parameters land somewhere real, and forwarded to the
      // store-scoped screen the merchant started on.
      ...(env.SOCIAL_IMPORT
        ? [
            {
              path: "settings/integrations",
              element: <SocialReturnLanding fallbackReturnTo="integrations" />,
            },
            {
              path: "products/new",
              element: (
                <SocialReturnLanding fallbackReturnTo="products/add-new-product" />
              ),
            },
          ]
        : []),

      {
        path: "templates/:templateId/preview",
        element: (
          <StudioRouteSwitch
            studioPath="/preview"
            templateIdParam="templateId"
            titleKey="templates.studio.previewTitle"
            fill="viewport"
            data-cy="studio-preview-frame"
          />
        ),
      },
      {
        // :templateId, not :templateVersionId — the Studio opens a template
        // the store already has by its STORE-TEMPLATE id (what every edit,
        // save and publish call takes) and one it does not by version id.
        // The host only relays the value, so the param name should not claim
        // to know which it is.
        path: "templates/:templateId/editor",
        element: (
          <StudioRouteSwitch
            studioPath="/edit"
            templateIdParam="templateId"
            titleKey="templates.studio.editorTitle"
            fill="viewport"
            data-cy="studio-editor-frame"
          />
        ),
      },

      {
        path: "store/:storeId",
        element: <RequirePermission />,
        children: [
          {
            index: true,
            element: <OnboardingGate />,
          },
          // Profile reads the signed-in user's own data — every authenticated
          // member should be able to see/edit their own profile, so no
          // resource-level gate beyond the parent's auth check.
          { path: "profile", element: <AdminProfile /> },
          // Store-level surfaces gate on store:update — only members with
          // permission to change store config should reach these pages.
          // Routes used to be bare children inheriting only the parent's
          // auth gate, which let any signed-in vendor staff land on
          // payment-settings / billing / templates / theme-settings and
          // see (or attempt to change) configuration they shouldn't.
          {
            path: "theme-settings",
            element: (
              <RouteGuard resource="store" action="update">
                <AdminThemeSettings />
              </RouteGuard>
            ),
          },
          {
            path: "settings",
            element: (
              <RouteGuard resource="store" action="update">
                <AdminStoreSettings />
              </RouteGuard>
            ),
          },
          // Connecting a social account changes store configuration, so it
          // gates on store:update like the rest of the settings surface.
          // Unmounted entirely while the social integration is hidden, so a
          // bookmarked /integrations falls through to the 404 rather than
          // reaching a screen with no way back to it.
          ...(env.SOCIAL_IMPORT
            ? [
                {
                  path: "integrations",
                  element: (
                    <RouteGuard resource="store" action="update">
                      <AdminIntegrations />
                    </RouteGuard>
                  ),
                },
              ]
            : []),
          {
            path: "notifications",
            element: (
              <RouteGuard resource="store" action="read">
                <NotificationsPage />
              </RouteGuard>
            ),
          },
          {
            path: "billing",
            element: (
              <RouteGuard resource="store" action="update">
                {/* Beta vendors (open-ended STARTER trial) have billing
                    hidden everywhere; this guard redirects direct URLs
                    back to the dashboard. */}
                <BetaBillingGuard>
                  <BillingPage />
                </BetaBillingGuard>
              </RouteGuard>
            ),
          },
          {
            path: "payment-settings",
            element: (
              <RouteGuard resource="store" action="update">
                <PaymentSettingsPage />
              </RouteGuard>
            ),
          },
          // Stripe Connect / billing return paths intentionally skip a
          // resource gate — they need to land for any vendor returning
          // from Stripe's hosted onboarding, even if the in-app role
          // mapping is mid-update. The parent's auth + active-store
          // checks still apply.
          { path: "stripe-return", element: <StripeReturnPage /> },
          { path: "stripe-refresh", element: <StripeRefreshPage /> },
          {
            path: "templates",
            element: (
              <RouteGuard resource="store" action="update">
                {/* Own Suspense boundary, ahead of DashboardLayout's generic
                    PageSkeleton one: the chunk's wait is the FIRST of this
                    route's loading states and has to match the two that
                    follow it, or the gallery visibly changes shape twice
                    before any content arrives. */}
                <Suspense fallback={<TemplateGallerySkeleton />}>
                  {/* Thin route page: resolves the store's business-type code
                      (onboarding choice) before rendering the gallery, so the
                      embedded Studio filters to it exactly like legacy. */}
                  <TemplatesRoutePage />
                </Suspense>
              </RouteGuard>
            ),
          },
          CustomerRoutes,
          UserRoutes,
          FeaturedProductsRoutes,
          CategoriesRoutes,
          MaterialsRoutes,
          RolesRoutes,
          OrderRoutes,
          ReturnRoutes,
          TransactionRoutes,
          AnalyticsRoutes,
          productRoute,
          variantRoute,
          MerchantLogisticsRoutes,
          MyRequestsRoutes,
          // Legal documents are store settings: the backend reads them with
          // store.read and writes with store.update (merchant-store-documents
          // controller). Ungated, a member with no store access was offered the
          // page — and until that controller was guarded, could publish it.
          { path: 'legal-support', element: (
            <RouteErrorBoundary>
              <RouteGuard resource="store" action="read">
                <Suspense fallback={<SuspenseLoading />}>
                  <VendorLegalSupportPage />
                </Suspense>
              </RouteGuard>
            </RouteErrorBoundary>
          ) },
          HelpSupportRoutes,
        ],
      },
      {
        path: "admin",
        element: <RequirePermission />,
        children: [
          { index: true, element: <SystemAdminHome /> },
          { path: "profile", element: <AdminProfile /> },
          { path: "notifications", element: <NotificationsPage /> },
          SystemStoreRoutes,
          SystemDocumentVerificationRoutes,
          SystemBetaAccessRoutes,
          SystemPlanRoutes,
          SystemSubscriptionRoutes,
          SystemVariantsRoutes,
          SystemSuperCategoryRoutes,
          SystemLogisticsRoutes,
          SystemPaymentRoutes,
          SystemAnalyticsRoutes,
          SystemMediaRoutes,
          SystemLegalSupportRoutes,
          SystemSupportTicketsRoutes,
          SystemSupportFaqRoutes,
        ],
      },
    ],
  },
  { path: "unauthorized_access", element: <UnAuthorized statusCode={403} /> },
  { path: "*", element: <NotFound /> },
]);
export default router;
